import React from 'react';
import i18n from 'util/i18n';
import { formConfig } from 'config/forms';
import { getNumberOfYearsString } from 'helpers/text';

import { Summary, DateSummary } from 'components/Summary';
import { SUBSTANCE_USE, SUBSTANCE_USE_DRUGS_PUBLIC_SAFETY } from 'config/formSections/substanceUse';
import Subsection from 'components/Section/shared/Subsection';
import connectSubsection from 'components/Section/shared/SubsectionConnector';
import Branch from 'components/Form/Branch';
import Show from 'components/Form/Show';
import Accordion from 'components/Form/Accordion';
import Comments from 'components/Form/Comments';
import { isPVQForm } from 'helpers/form';
import DrugPublicSafetyUse from './DrugPublicSafetyUse';

const sectionConfig = {
  key: SUBSTANCE_USE_DRUGS_PUBLIC_SAFETY.key,
  section: SUBSTANCE_USE.name,
  store: SUBSTANCE_USE.store,
  subsection: SUBSTANCE_USE_DRUGS_PUBLIC_SAFETY.name,
  storeKey: SUBSTANCE_USE_DRUGS_PUBLIC_SAFETY.storeKey,
};

export class DrugPublicSafetyUses extends Subsection {
  constructor(props) {
    super(props);

    const {
      section, subsection, store, storeKey
    } = sectionConfig;

    this.section = section;
    this.subsection = subsection;
    this.store = store;
    this.storeKey = storeKey;
    this.updateList = this.updateList.bind(this);
    this.updateUsedDrugs = this.updateUsedDrugs.bind(this);
    this.summary = this.summary.bind(this);
  }

  update = (updateValues) => {
    this.props.onUpdate(this.storeKey, {
      UsedDrugs: this.props.UsedDrugs,
      List: this.props.List,
      Comments: this.props.Comments,
      SummaryComments: this.props.SummaryComments,
      ...updateValues,
    });
  };

  updateList = (values) => {
    this.update({
      List: values,
    });
  };

  updateUsedDrugs = (values) => {
    this.update({
      UsedDrugs: values,
      List: values.value === 'Yes' ? this.props.List : [],
      SummaryComments: values.value === 'Yes' ? this.props.SummaryComments : {}
    });
  };

  updateComments = (values) => {
    this.update({
      Comments: values,
    });
  };

  updateSummaryComments = (values) => {
    this.update({
      SummaryComments: values,
    });
  };

  summary = (item, index) => {
    const o = (item || {}).Item || {};
    const range = DateSummary(o.InvolvementDates);
    const description = (o.Description || {}).value;

    return Summary({
      type: i18n.t('substance.drugs.clearance.collection.itemType'),
      index,
      left: description,
      right: range,
      placeholder: i18n.t('substance.drugs.clearance.collection.summary'),
    });
  };

  render() {
    const { formType, errors } = this.props;
    const formTypeConfig = formType && formConfig[formType];
    const years = formTypeConfig && formTypeConfig.SUBSTANCE_DRUG_PUBLIC_SAFETY_YEARS;
    const isPvq = isPVQForm(this.props.formType);
    let branchLabelCopy;
    if (years === 'EVER') {
      branchLabelCopy = isPvq ? i18n.t('substance.pvq.drugs.heading.drugPublicSafetyUses') : i18n.t('substance.drugs.heading.drugPublicSafetyUses');
    } else {
      const numberOfYearsString = getNumberOfYearsString(years);
      branchLabelCopy = isPvq ? i18n.t('substance.pvq.drugs.heading.drugPublicSafetyUsesNum', {
        numberOfYearsString,
      }) : i18n.t('substance.drugs.heading.drugPublicSafetyUsesNum', {
        numberOfYearsString,
      });
    }

    const accordionErrors = errors && errors.filter((e) => e.indexOf('List.accordion') === 0);
    return isPvq ? (
      <div
        className="section-content drug-public-safety-uses"
        data-section={SUBSTANCE_USE.key}
        data-subsection={SUBSTANCE_USE_DRUGS_PUBLIC_SAFETY.key}
      >
        <h1 className="section-header">{i18n.t('substance.pvq.subsection.drugs.publicsafety')}</h1>
        <Branch
          attribute="UsedDrugs"
          name="UsedDrugs"
          label={branchLabelCopy}
          labelSize="h4"
          className="used-drugs"
          {...this.props.UsedDrugs}
          warning
          onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
          required={this.props.required}
          onUpdate={this.updateUsedDrugs}
          scrollIntoView={this.props.scrollIntoView}
        />
        <Show when={this.props.UsedDrugs.value === 'Yes'}>
          <Accordion
            attribute="List"
            defaultState={this.props.defaultState}
            {...this.props.List}
            scrollToBottom={this.props.scrollToBottom}
            summary={this.summary}
            onUpdate={this.updateList}
            onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
            errors={accordionErrors}
            description={i18n.t('substance.pvq.drugs.publicSafety.collection.description')}
            appendTitle={i18n.t('substance.pvq.drugs.publicSafety.collection.appendTitle')}
            appendLabel={i18n.t('substance.pvq.drugs.publicSafety.collection.appendLabel')}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          >
            <DrugPublicSafetyUse
              name="Item"
              bind
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              formType={this.props.formType}
            />
          </Accordion>
        </Show>
      </div>
    ) : (
      <div
        className="section-content drug-public-safety-uses"
        data-section={SUBSTANCE_USE.key}
        data-subsection={SUBSTANCE_USE_DRUGS_PUBLIC_SAFETY.key}
      >
        <h1 className="section-header">{i18n.t('substance.subsection.drugs.publicsafety')}</h1>
        <Branch
          attribute="UsedDrugs"
          name="UsedDrugs"
          label={branchLabelCopy}
          labelSize="h4"
          className="used-drugs"
          {...this.props.UsedDrugs}
          warning
          onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
          required={this.props.required}
          onUpdate={this.updateUsedDrugs}
          scrollIntoView={this.props.scrollIntoView}
        />
        <Comments
          title={i18n.t('substance.comments.optional')}
          name="Comments"
          className="optional-comment"
          attribute="Comments"
          {...this.props.Comments}
          onUpdate={this.updateComments}
          required={false}
        />
        <Show when={this.props.UsedDrugs.value === 'Yes'}>
          <Accordion
            attribute="List"
            defaultState={this.props.defaultState}
            {...this.props.List}
            scrollToBottom={this.props.scrollToBottom}
            summary={this.summary}
            onUpdate={this.updateList}
            onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
            errors={accordionErrors}
            description={i18n.t('substance.drugs.publicSafety.collection.description')}
            appendTitle={i18n.t('substance.drugs.publicSafety.collection.appendTitle')}
            appendLabel={i18n.t('substance.drugs.publicSafety.collection.appendLabel')}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          >
            <DrugPublicSafetyUse
              name="Item"
              bind
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
            />
          </Accordion>
          <Comments
            title={i18n.t('substance.comments.summary')}
            name="SummaryComments"
            className="summary-comment"
            attribute="SummaryComments"
            {...this.props.SummaryComments}
            onUpdate={this.updateSummaryComments}
            required={false}
          />
        </Show>
      </div>
    );
  }
}

DrugPublicSafetyUses.defaultProps = {
  UsedDrugs: {},
  List: { items: [], branch: {} },
  onError: (value, arr) => arr,
  section: 'substance',
  subsection: 'drugs/publicsafety',
  dispatch: () => { },
  scrollToBottom: '',
  errors: [],
  formType: '',
};

export default connectSubsection(DrugPublicSafetyUses, sectionConfig);
